import type { ReactNode } from 'react';
import clsx from 'clsx';

import styles from './ActivityCard.module.scss';
import { ACTIVITY_PILL_TONE_CLASS } from './constants';
import type { ActivityPillTone } from './constants';
import { arrowTopRightWhite } from '../../../../assets/hostedassets';
import PillArrowGlyph from './PillArrowGlyph';

export type { ActivityPillTone } from './constants';

interface ActivityCountPillProps {
    /** Colour family — red for alerts, blue for connections. Ignored when `muted`. */
    tone: ActivityPillTone;
    /** Count shown by default. Callers pass their own placeholder while loading. */
    count: ReactNode;
    /** Empty / not-yet-loaded: greys the pill and its arrow. */
    muted?: boolean;
    /**
     * Swaps in for the count on hover, on every surface that renders the pill. Omit
     * it to keep the count pinned — with no element rendered there is nothing to swap.
     */
    hoverText?: string;
    /** Mobile cards render the pill as a full-width row beneath the title. */
    fullWidth?: boolean;
    /**
     * Draw the arrow as the compact single-glyph badge instead of the cards' 1rem
     * coloured circle. Used by the sidebar rows, where the card badge is too big.
     */
    compactArrow?: boolean;
}

/**
 * The count pill shared by every surface that offers an Alerts / Connections
 * drawer — the Overview cards (desktop + mobile) and the company sidebar rows.
 * One component so the button cannot drift between them.
 */
function ActivityCountPill({
    tone,
    count,
    muted = false,
    hoverText,
    fullWidth = false,
    compactArrow = false,
}: Readonly<ActivityCountPillProps>) {
    return (
        <span
            className={clsx(styles.Pill, muted ? styles.PillMuted : ACTIVITY_PILL_TONE_CLASS[tone], {
                [styles.PillFullWidth]: fullWidth,
            })}
        >
            <span className={styles.PillCount}>{count}</span>
            {hoverText && <span className={styles.PillHoverText}>{hoverText}</span>}
            {compactArrow ? (
                <PillArrowGlyph className={styles.PillArrowGlyph} />
            ) : (
                <span className={styles.PillArrowCircle}>
                    <img src={arrowTopRightWhite} alt="" className={styles.PillArrowIcon} />
                </span>
            )}
        </span>
    );
}

export default ActivityCountPill;
